import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { PostHogInterface } from "posthog-js";
import { PostHogContext, type PostHogContextValue } from "./context";
import PostHogPageView from "./pageview";
import { scheduleIdle } from "@/lib/utils/scheduleIdle";

const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

function isLocalhost() {
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1" || host === "[::1]";
}

function maskEmailsInReplayText(text: string) {
  if (text.trim().length === 0) return text;
  return text.replace(
    /(\S+)@(\S+\.\S+)/g,
    (_match, local: string, domain: string) =>
      `${"*".repeat(local.length)}@${"*".repeat(domain.length)}`,
  );
}

export default function TrainrrPostHogProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [client, setClient] = useState<PostHogInterface | null>(null);
  const [flagsRevision, setFlagsRevision] = useState(0);
  const initStarted = useRef(false);

  useEffect(() => {
    if (!POSTHOG_KEY || initStarted.current) return;
    initStarted.current = true;
    const apiKey = POSTHOG_KEY;

    const start = () => {
      const recordSessions = !isLocalhost();
      const posthogImport = import("posthog-js");
      const recorderImport = recordSessions
        ? import("posthog-js/dist/posthog-recorder")
        : Promise.resolve();

      void Promise.all([posthogImport, recorderImport]).then(
        ([{ default: posthog }]) => {
          posthog.init(apiKey, {
            api_host: POSTHOG_HOST,
            ui_host: "https://eu.posthog.com",
            capture_pageview: false,
            capture_dead_clicks: false,
            capture_heatmaps: false,
            disable_session_recording: !recordSessions,
            disable_surveys: true,
            disable_external_dependency_loading: true,
            enable_recording_console_log: false,
            session_recording: recordSessions
              ? {
                  maskAllInputs: true,
                  maskTextSelector: "*",
                  maskTextFn: maskEmailsInReplayText,
                  recordHeaders: false,
                  recordBody: false,
                  recordCrossOriginIframes: false,
                  maskCapturedNetworkRequestFn: (request) => {
                    const queryIndex = request.name.indexOf("?");
                    if (queryIndex !== -1) {
                      request.name = request.name.slice(0, queryIndex);
                    }
                    return request;
                  },
                }
              : undefined,
            loaded: (ph) => {
              setClient(ph);
              setFlagsRevision((revision) => revision + 1);
            },
          });
        },
      );
    };

    return scheduleIdle(start);
  }, []);

  useEffect(() => {
    if (!client) return;
    return client.onFeatureFlags(() => {
      setFlagsRevision((revision) => revision + 1);
    });
  }, [client]);

  const value = useMemo<PostHogContextValue>(
    () => ({ client, flagsRevision }),
    [client, flagsRevision],
  );

  return (
    <PostHogContext.Provider value={value}>
      <PostHogPageView />
      {children}
    </PostHogContext.Provider>
  );
}
